import { useCallback, useEffect, useState } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  hydrateNotificationPreferences,
  patchNotificationPreferences,
} from 'Pods/ThreeDStudio/Store/actions/notificationPreferences'

function browserSupportsWebPush() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
}

const pushCleverPushCommand = (command) => {
  window.CleverPush = window.CleverPush || []
  window.CleverPush.push(command)
}

// Shared state for the CGTrader AI notification toggles (first-generation popover
// and Settings modal). Toggles persist to localStorage immediately. Turning the
// browser toggle on defers to the CleverPush SDK opt-in flow and only persists once
// the SDK confirms a subscription, so the stored flag never claims push permission
// the browser did not grant.
export default function useNotificationPreferences() {
  const pageConfig = usePageConfig()
  const cleverPushEnabled = Boolean(pageConfig?.cleverPushEnabled)
  const currentUserId = pageConfig?.currentUserId
  const preferences = useThreeDStudioStore(state => state.notificationPreferences)
  const [isBrowserPushSupported, setIsBrowserPushSupported] = useState(false)

  useEffect(() => {
    hydrateNotificationPreferences()
  }, [])

  useEffect(() => {
    setIsBrowserPushSupported(cleverPushEnabled && browserSupportsWebPush())
  }, [cleverPushEnabled])

  // Re-tag users who opted in before the user_id attribute was wired up, and
  // re-tag the subscription when the browser switches accounts -- otherwise
  // Cleverpush::Notifications::SendRunSucceeded keeps targeting the previous
  // account. The CleverPush queue ignores `setAttribute` for anonymous sessions,
  // so this is safe to push unconditionally once the SDK initialises (an
  // opted-out device has no subscription left to tag). The effect re-runs when
  // `currentUserId` becomes available so late-arriving page config (e.g. via the
  // `window-config-loaded` event) is honored.
  useEffect(() => {
    if (!cleverPushEnabled || !currentUserId) return

    pushCleverPushCommand(['setAttribute', 'user_id', String(currentUserId)])
  }, [cleverPushEnabled, currentUserId])

  const setEmailWhenReady = useCallback((enabled) => {
    patchNotificationPreferences({ emailWhenReady: enabled })
  }, [])

  const setBrowserNotifications = useCallback((enabled) => {
    if (!enabled) {
      // The run-succeeded worker targets subscriptions by their user_id attribute,
      // so an opt-out has to clear that attribute and drop the subscription itself;
      // persisting the flag alone leaves the notifications arriving. Order matters:
      // the attribute can only be cleared while the subscription still exists.
      if (cleverPushEnabled) {
        pushCleverPushCommand(['setAttribute', 'user_id', ''])
        pushCleverPushCommand(['unsubscribe'])
      }
      patchNotificationPreferences({ browserNotifications: false })
      return
    }

    pushCleverPushCommand(['triggerOptIn', (err, subscriptionId) => {
      if (err || !subscriptionId) return
      patchNotificationPreferences({ browserNotifications: true })
    }])
    if (currentUserId) {
      pushCleverPushCommand(['setAttribute', 'user_id', String(currentUserId)])
    }
  }, [cleverPushEnabled, currentUserId])

  return {
    preferences,
    isBrowserPushSupported,
    setEmailWhenReady,
    setBrowserNotifications,
  }
}
